import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { usePhishPass } from '../../context/PhishPassContext';
import { ShieldCheck, AlertTriangle, Search, Loader2, GraduationCap } from 'lucide-react';

const riskColor = (level) => ({
  High: 'bg-red-100 text-red-700 border-red-200',
  Medium: 'bg-amber-100 text-amber-700 border-amber-200',
  Low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
})[level] || 'bg-slate-100 text-slate-600';

const RecipientRiskView = () => {
  const { fetchRecipientsRisk, markTrained } = usePhishPass();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | high | medium | low
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await fetchRecipientsRisk();
      if (!cancelled) {
        setRows(data);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchRecipientsRisk, reloadKey]);

  const handleTrained = async (email) => {
    const r = await markTrained(email);
    if (r.success) { toast.success(`Marked ${email} as trained`); setReloadKey(k => k + 1); }
    else toast.error('Could not mark trained');
  };

  const filtered = rows.filter(r => {
    if (filter !== 'all' && r.risk_level !== filter.charAt(0).toUpperCase() + filter.slice(1)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="risk-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Employee Risk Scores</h2>
          <p className="text-sm text-slate-500">Aggregated click + open behavior across all campaigns.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email" className="pl-7 w-64" data-testid="risk-search" />
          </div>
          {['all', 'high', 'medium', 'low'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} data-testid={`risk-filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">
          No tracked employees yet. Send a campaign to start collecting data.
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs text-slate-500 uppercase">
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3 text-center">Sent</th>
                  <th className="px-4 py-3 text-center">Opened</th>
                  <th className="px-4 py-3 text-center">Clicked</th>
                  <th className="px-4 py-3">Last Click</th>
                  <th className="px-4 py-3">Risk</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.email} className="border-b hover:bg-slate-50" data-testid={`risk-row-${r.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{r.name || r.email}</div>
                      <div className="text-xs text-slate-500">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 text-center">{r.total_sent}</td>
                    <td className="px-4 py-3 text-center">{r.total_opened}</td>
                    <td className="px-4 py-3 text-center font-semibold">{r.total_clicked}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.last_clicked_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge className={`${riskColor(r.risk_level)} border`}>
                        {r.risk_level === 'High' ? <AlertTriangle className="w-3 h-3 mr-1 inline" /> : null}
                        {r.risk_level}
                      </Badge>
                      {r.trained_marked && <Badge variant="outline" className="ml-2 text-xs text-emerald-700"><ShieldCheck className="w-3 h-3 mr-0.5" />Trained</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.risk_level !== 'Low' && (
                        <Button size="sm" variant="outline" onClick={() => handleTrained(r.email)} data-testid={`mark-trained-${r.email}`}>
                          <GraduationCap className="w-3.5 h-3.5 mr-1" />Mark Trained
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RecipientRiskView;
